import React, { useMemo, useState } from 'react';
import { Responsive, WidthProvider, Layout as RGLLayout } from 'react-grid-layout';
import { WidgetContainer } from './WidgetContainer';
import { WidgetId, WidgetLayout, Role } from '@/types'; 
import { WIDGET_CONSTRAINTS } from '../utils/layoutDefaults';
import { Shield, Users, Layers } from 'lucide-react';
import clsx from 'clsx';

// 🏗️ Higher-order component to inject responsive width detection
const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardGridProps {
  layouts: WidgetLayout[];
  isEditMode: boolean;
  onLayoutChange: (currentLayout: RGLLayout[]) => void;
  onRemoveWidget: (id: string) => void;
  widgetRegistry: Partial<Record<WidgetId, React.ReactNode>>;
  
  // 🛡️ Hybrid Scope Props
  currentUserId: string;
  currentHouseholdId: string | null;
  activeScope: 'PERSONAL' | 'SHARED' | 'BOTH';
}

/**
 * 🛰️ DashboardGrid
 * Refactored for Hybrid Scoping: The layout itself is personal (/users/{uid}),
 * but widgets consume data based on the activeScope toggle.
 */
export const DashboardGrid: React.FC<DashboardGridProps> = ({
  layouts,
  isEditMode,
  onLayoutChange,
  onRemoveWidget,
  widgetRegistry,
  currentUserId,
  currentHouseholdId,
  activeScope
}) => {

  /**
   * 🧠 Layout Transformer
   * Converts WidgetLayout[] to RGL's Layout[] format.
   * Layouts are unique to the user's personal partition.
   */
  const rglLayout = useMemo(() => {
    return (layouts || []).map((w: WidgetLayout) => ({
      i: w.i,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      ...WIDGET_CONSTRAINTS[w.i as WidgetId] 
    }));
  }, [layouts]);

  return (
    <div className={clsx(
      "flex-1 transition-all duration-700 min-h-screen relative",
      isEditMode ? 'bg-slate-50/80 dark:bg-slate-950/40' : 'bg-transparent'
    )}>
      
      {/* 🛡️ SCOPE STATUS BAR (Visual Confirmation of Data Source) */}
      {!isEditMode && (
        <div className="absolute top-4 left-8 z-20 flex items-center gap-2 px-4 py-1.5 bg-white/50 dark:bg-slate-900/50 backdrop-blur-md rounded-full border border-slate-200 dark:border-slate-800 animate-in fade-in slide-in-from-top-2">
          {activeScope === 'PERSONAL' || !currentHouseholdId ? (
            <Shield className="w-3 h-3 text-emerald-500" />
          ) : activeScope === 'SHARED' ? (
            <Users className="w-3 h-3 text-indigo-500" />
          ) : (
            <Layers className="w-3 h-3 text-violet-500" />
          )}
          <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500">
            {currentHouseholdId ? `${activeScope} Data Stream Active` : 'Personal Partition Active'}
          </span>
        </div>
      )}

      {/* --- GRID ENGINE --- */}
      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: rglLayout, md: rglLayout }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 30, md: 25, sm: 19, xs: 12, xxs: 8 }}
        rowHeight={40}
        draggableHandle=".drag-handle"
        isDraggable={isEditMode}
        isResizable={isEditMode}
        margin={[0, 0]}
        onLayoutChange={onLayoutChange}
        useCSSTransforms={true}
        compactType={null}
        preventCollision={true}
      >
        {layouts.map((widget: WidgetLayout) => (
          <div key={widget.i} className="relative">
            <WidgetContainer
              id={widget.i as WidgetId}
              title={(widget.i || '').replace(/_/g, ' ')}
              isEditMode={isEditMode}
              onRemove={onRemoveWidget}
              dragHandleProps={{ className: 'drag-handle' }}
              // 🎨 Scope-aware container styling
              className={clsx(
                activeScope === 'PERSONAL' && "border-emerald-500/20",
                activeScope === 'SHARED' && "border-indigo-500/20"
              )}
            >
              {/* 🧩 Content Injection with Scope Awareness */}
              {widgetRegistry[widget.i as WidgetId] || (
                <div className="flex flex-col items-center justify-center h-full opacity-20 italic">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Module Offline
                  </p>
                </div>
              )}
            </WidgetContainer>
          </div>
        ))}
      </ResponsiveGridLayout>

      {/* --- EDIT MODE VISUAL GUIDES --- */}
      {isEditMode && (
        <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.05] dark:opacity-[0.3] transition-opacity duration-1000">
          <div
            className="h-full w-full"
            style={{
              backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
              backgroundSize: `40px 40px`,
              color: 'rgb(79, 70, 229)'
            }}
          />
        </div>
      )}
    </div>
  );
};